import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, CircularProgress, Link, Popover, Stack } from "@mui/material";
import { useGetCBHSpecialist } from "@src/appV2/ContactCBHInfo/api/useGetCBHSpecialist";

import {
  BILLING_REP_EMAIL,
  CbhSpecialistType,
  SUPPORT_TELEPHONE,
} from "@src/appV2/ContactCBHInfo/constants";

import { getCBHSpecialistProfile } from "@src/appV2/ContactCBHInfo/utils/getCBHSpecialistProfile";
import { useCurrentUserId } from "@src/appV2/ExperimentalHomeHealth/lib/useCurrentUserId";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { BottomSheet } from "@src/appV2/redesign/components/BottomSheet";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogHeader } from "@src/appV2/redesign/components/DialogHeader";
import { Text } from "@src/appV2/redesign/components/Text";

import {
  getBorderBase,
  getMutedText,
  getStrongText,
} from "@src/appV2/redesign/HelpCenter/themeHelpers";

import { useThemeMode } from "@src/appV2/UserPreferences/UserPreferencesProvider";
import { useModalState } from "@src/lib/ui-react";
import { useState } from "react";

const cardSx = (theme: Parameters<typeof getBorderBase>[0]) => ({
  borderRadius: theme.borderRadius?.xLarge,
  border: `1px solid ${getBorderBase(theme)}`,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
  boxShadow: theme.shadow?.card,
});

interface SpecialistSectionProps {
  title: string;
  workTimings: string;
  name: string;
  phone?: string;
  email?: string;
}

function SpecialistSection({ title, workTimings, name, phone, email }: SpecialistSectionProps) {
  return (
    <Box sx={cardSx}>
      <Stack spacing={2}>
        <Box>
          <Text
            bold
            variant="body1"
            sx={(theme) => ({ color: getStrongText(theme), lineHeight: 1.2 })}
          >
            {title}
          </Text>
          {workTimings.length > 0 && (
            <Text variant="caption" sx={(theme) => ({ color: getMutedText(theme) })}>
              {workTimings}
            </Text>
          )}
        </Box>
        <Text variant="body2" sx={(theme) => ({ color: getStrongText(theme) })}>
          {name}
        </Text>
        <Stack spacing={1}>
          {phone && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <PhoneOutlinedIcon
                fontSize="small"
                sx={(theme) => ({ color: getMutedText(theme) })}
              />
              <Link href={`tel:${phone}`} underline="hover" variant="body2">
                {phone}
              </Link>
            </Stack>
          )}
          {email && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <EmailOutlinedIcon
                fontSize="small"
                sx={(theme) => ({ color: getMutedText(theme) })}
              />
              <Link href={`mailto:${email}`} underline="hover" variant="body2">
                {email}
              </Link>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

interface ContactSectionsProps {
  isLoading: boolean;
  amSpecialist: { name: string; phone?: string; email?: string; workTimings: string } | undefined;
  billingSpecialist:
    | { name: string; phone?: string; email?: string; workTimings: string }
    | undefined;
}

function ContactSections({ isLoading, amSpecialist, billingSpecialist }: ContactSectionsProps) {
  const { tu } = useTu("navigation");

  return (
    <Stack spacing={2} sx={{ padding: 3 }}>
      <Box sx={cardSx}>
        <Stack spacing={2}>
          <Text
            bold
            variant="body1"
            sx={(theme) => ({ color: getStrongText(theme), lineHeight: 1.2 })}
          >
            {tu("24/7 Support")}
          </Text>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PhoneOutlinedIcon fontSize="small" sx={(theme) => ({ color: getMutedText(theme) })} />
            <Link href={`tel:${SUPPORT_TELEPHONE}`} underline="hover" variant="body2">
              {SUPPORT_TELEPHONE}
            </Link>
          </Stack>
        </Stack>
      </Box>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", paddingY: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {amSpecialist && (
        <SpecialistSection
          title={tu("Account Manager")}
          workTimings={amSpecialist.workTimings}
          name={amSpecialist.name}
          phone={amSpecialist.phone}
          email={amSpecialist.email}
        />
      )}
      {billingSpecialist && (
        <SpecialistSection
          title={tu("Billing Specialist")}
          workTimings={billingSpecialist.workTimings}
          name={billingSpecialist.name}
          phone={billingSpecialist.phone}
          email={billingSpecialist.email}
        />
      )}
    </Stack>
  );
}

export function ContactSupportButton() {
  const { tu } = useTu("navigation");

  const isMobile = useIsMobile();
  const { themeMode } = useThemeMode();
  const isDark = themeMode === "dark";
  const navLinkVariant = isDark ? "linkInverted" : "link";
  const workplaceId = useCurrentUserId() ?? undefined;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hasOpened, setHasOpened] = useState(false);
  const bottomSheetState = useModalState();

  const { data: amData, isLoading: isLoadingAm } = useGetCBHSpecialist({
    facilityId: hasOpened ? workplaceId : undefined,
    type: CbhSpecialistType.ACCOUNT_MANAGER,
  });

  const { data: billingData, isLoading: isLoadingBilling } = useGetCBHSpecialist({
    facilityId: hasOpened ? workplaceId : undefined,
    type: CbhSpecialistType.BILLING_SPECIALIST,
  });

  const amProfile = isDefined(amData)
    ? getCBHSpecialistProfile(amData, CbhSpecialistType.ACCOUNT_MANAGER)
    : undefined;

  const amSpecialist =
    isDefined(amProfile) && amProfile.name
      ? {
          name: amProfile.name,
          phone: amProfile.phone,
          email: amProfile.email,
          workTimings: amProfile.workTimings,
        }
      : undefined;

  const billingProfile = isDefined(billingData)
    ? getCBHSpecialistProfile(billingData, CbhSpecialistType.BILLING_SPECIALIST)
    : undefined;

  const billingSpecialist =
    isDefined(billingProfile) && billingProfile.name
      ? {
          name: billingProfile.name,
          phone: billingProfile.phone ?? SUPPORT_TELEPHONE,
          email: billingProfile.email ?? BILLING_REP_EMAIL,
          workTimings: billingProfile.workTimings,
        }
      : undefined;

  const isLoading = isLoadingAm || isLoadingBilling;
  const isOpen = isMobile ? bottomSheetState.modalIsOpen : Boolean(anchorEl);

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setHasOpened(true);
    if (isMobile) {
      bottomSheetState.openModal();
    } else {
      setAnchorEl(event.currentTarget);
    }
  }

  return (
    <>
      <Button
        variant={navLinkVariant}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={handleClick}
      >
        <PhoneOutlinedIcon sx={{ fontSize: "1rem" }} />
        {tu("Contact Support")}
      </Button>
      {isMobile ? (
        <BottomSheet modalState={bottomSheetState}>
          <DialogHeader
            title={tu("Contact Support")}
            showCloseButton
            onClose={bottomSheetState.closeModal}
          />
          <Box sx={{ overflowY: "auto" }}>
            <ContactSections
              isLoading={isLoading}
              amSpecialist={amSpecialist}
              billingSpecialist={billingSpecialist}
            />
          </Box>
        </BottomSheet>
      ) : (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 2,
              minWidth: 280,
            },
          }}
        >
          <ContactSections
            isLoading={isLoading}
            amSpecialist={amSpecialist}
            billingSpecialist={billingSpecialist}
          />
        </Popover>
      )}
    </>
  );
}
